import { moveAsset } from "@/services/asset-service";
import {
	DynamicLocationPicker,
} from "@/components/inputs/dynamic-location-input";
import { getLocation } from "@/services/location-service";
import {
	CheckCircleOutlined,
	EnvironmentOutlined,
	SwapOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	Alert,
	Button,
	Descriptions,
	Input,
	Modal,
	Radio,
	Space,
	Steps,
	Tag,
	Typography,
	message,
} from "antd";
import { useState } from "react";

const { Text, Title } = Typography;
const { TextArea } = Input;

interface MoveAssetModalProps {
	visible: boolean;
	onClose: () => void;
	onSuccess: () => void;
	asset: {
		id: number;
		assetTag?: string;
		location?: {
			id: number;
			name: string;
			address1?: string;
			city?: string;
			state?: string;
			storeNumber?: string;
		};
	};
}

const MoveAssetModal = ({
	visible,
	onClose,
	onSuccess,
	asset,
}: MoveAssetModalProps) => {
	const [currentStep, setCurrentStep] = useState(0);
	const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
	const [selectedLocationDisplay, setSelectedLocationDisplay] = useState<string>("");
	const [reason, setReason] = useState<"error_correction" | "physical_move" | null>(null);
	const [notes, setNotes] = useState("");
	const [loading, setLoading] = useState(false);

	const resetState = () => {
		setCurrentStep(0);
		setSelectedLocationId(null);
		setSelectedLocationDisplay("");
		setReason(null);
		setNotes("");
		setLoading(false);
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const handleNext = async () => {
		if (currentStep === 0 && !selectedLocationId) {
			message.warning("Please select a destination location");
			return;
		}
		if (currentStep === 1 && !reason) {
			message.warning("Please select a reason for moving the asset");
			return;
		}
		// Fetch destination location display name before showing confirmation
		if (currentStep === 1 && selectedLocationId && !selectedLocationDisplay) {
			try {
				const loc = await getLocation(selectedLocationId);
				const display = loc
					? `${loc.storeNumber || loc.store_number || loc.name || ""} - ${loc.address1 || ""}${loc.city ? `, ${loc.city}` : ""}${loc.state ? ` ${loc.state}` : ""}`
					: `Location #${selectedLocationId}`;
				setSelectedLocationDisplay(display);
			} catch {
				setSelectedLocationDisplay(`Location #${selectedLocationId}`);
			}
		}
		setCurrentStep((prev) => prev + 1);
	};

	const handleBack = () => {
		setCurrentStep((prev) => prev - 1);
	};

	const handleConfirm = async () => {
		if (!selectedLocationId || !reason) return;

		setLoading(true);
		try {
			await moveAsset(
				asset.id,
				parseInt(selectedLocationId),
				reason,
				notes || undefined,
			);
			message.success("Asset moved successfully!");
			resetState();
			onSuccess();
		} catch (error: any) {
			message.error(error?.message || "Failed to move asset");
		} finally {
			setLoading(false);
		}
	};

	const currentLocationDisplay = asset.location
		? `${asset.location.storeNumber || asset.location.name || ""} - ${asset.location.address1 || ""}${asset.location.city ? `, ${asset.location.city}` : ""}${asset.location.state ? ` ${asset.location.state}` : ""}`
		: "Unknown Location";

	const reasonLabel =
		reason === "error_correction"
			? "Error Correction"
			: reason === "physical_move"
				? "Physically Moved"
				: "";

	const steps = [
		{
			title: "Select Location",
			description: "Choose destination",
		},
		{
			title: "Reason",
			description: "Why is it moving?",
		},
		{
			title: "Confirm",
			description: "Review and confirm",
		},
	];

	return (
		<Modal
			title={
				<Space>
					<SwapOutlined />
					<span>Move Asset</span>
				</Space>
			}
			open={visible}
			onCancel={handleClose}
			width={600}
			footer={
				<div style={{ display: "flex", justifyContent: "space-between" }}>
					<div>
						{currentStep > 0 && (
							<Button onClick={handleBack} disabled={loading}>
								Back
							</Button>
						)}
					</div>
					<Space>
						<Button onClick={handleClose} disabled={loading}>
							Cancel
						</Button>
						{currentStep < 2 ? (
							<Button type="primary" onClick={handleNext}>
								Next
							</Button>
						) : (
							<Button
								type="primary"
								onClick={handleConfirm}
								loading={loading}
								icon={<CheckCircleOutlined />}
							>
								Confirm Move
							</Button>
						)}
					</Space>
				</div>
			}
			destroyOnClose
		>
			<Steps
				current={currentStep}
				items={steps}
				size="small"
				style={{ marginBottom: 24 }}
			/>

			{/* Step 1: Select Destination Location */}
			{currentStep === 0 && (
				<div>
					<div style={{ marginBottom: 16 }}>
						<Text strong>Current Location:</Text>
						<div
							style={{
								padding: "8px 12px",
								background: "#f5f5f5",
								borderRadius: 6,
								marginTop: 4,
								display: "flex",
								alignItems: "center",
								gap: 8,
							}}
						>
							<EnvironmentOutlined style={{ color: "#1890ff" }} />
							<Text>{currentLocationDisplay}</Text>
						</div>
					</div>

					<div style={{ marginBottom: 8 }}>
						<Text strong>New Location:</Text>
					</div>
					<DynamicLocationPicker
						value={selectedLocationId}
						onChange={(value: string | null) => {
							setSelectedLocationId(value);
						}}
						disabled={false}
						placeholder="Search for a location..."
					/>
					{selectedLocationId && (
						<div style={{ marginTop: 8 }}>
							<Tag color="green" icon={<EnvironmentOutlined />}>
								Location selected
							</Tag>
						</div>
					)}
				</div>
			)}

			{/* Step 2: Select Reason */}
			{currentStep === 1 && (
				<div>
					<div style={{ marginBottom: 16 }}>
						<Text strong>Why is this asset being moved?</Text>
					</div>
					<Radio.Group
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						style={{ width: "100%" }}
					>
						<Space direction="vertical" style={{ width: "100%" }}>
							<Radio.Button
								value="error_correction"
								style={{
									width: "100%",
									height: "auto",
									padding: "12px 16px",
									textAlign: "left",
									whiteSpace: "normal",
								}}
							>
								<div>
									<div style={{ fontWeight: 600 }}>
										<WarningOutlined style={{ marginRight: 8, color: "#faad14" }} />
										Error Correction
									</div>
									<Text type="secondary" style={{ fontSize: 12 }}>
										The asset was incorrectly placed at the wrong location and needs
										to be corrected.
									</Text>
								</div>
							</Radio.Button>
							<Radio.Button
								value="physical_move"
								style={{
									width: "100%",
									height: "auto",
									padding: "12px 16px",
									textAlign: "left",
									whiteSpace: "normal",
								}}
							>
								<div>
									<div style={{ fontWeight: 600 }}>
										<SwapOutlined style={{ marginRight: 8, color: "#1890ff" }} />
										Physically Moved
									</div>
									<Text type="secondary" style={{ fontSize: 12 }}>
										The asset was physically relocated from one location to
										another.
									</Text>
								</div>
							</Radio.Button>
						</Space>
					</Radio.Group>

					<div style={{ marginTop: 16 }}>
						<Text strong>Notes (optional):</Text>
						<TextArea
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							placeholder="Add any additional context about this move..."
							rows={3}
							style={{ marginTop: 4 }}
						/>
					</div>
				</div>
			)}

			{/* Step 3: Confirmation */}
			{currentStep === 2 && (
				<div>
					<Alert
						message="Please review the move details before confirming"
						type="info"
						showIcon
						style={{ marginBottom: 16 }}
					/>

					<Descriptions column={1} bordered size="small">
						<Descriptions.Item label="Asset">
							<Text strong>{asset.assetTag || `Asset #${asset.id}`}</Text>
						</Descriptions.Item>
						<Descriptions.Item label="From">
							<Tag color="red">{currentLocationDisplay}</Tag>
						</Descriptions.Item>
						<Descriptions.Item label="To">
							<Tag color="green">
								{selectedLocationDisplay || `Location #${selectedLocationId}`}
							</Tag>
						</Descriptions.Item>
						<Descriptions.Item label="Reason">
							<Tag color={reason === "error_correction" ? "orange" : "blue"}>
								{reasonLabel}
							</Tag>
						</Descriptions.Item>
						{notes && (
							<Descriptions.Item label="Notes">
								<Text>{notes}</Text>
							</Descriptions.Item>
						)}
					</Descriptions>

					<Alert
						message="This action will update the asset's location and notify any connected integrations."
						type="warning"
						showIcon
						style={{ marginTop: 16 }}
					/>
				</div>
			)}
		</Modal>
	);
};

export default MoveAssetModal;
